import { ActionIcon, Avatar, Badge, Box, Button, Center, Group, Loader, Menu, Text, Tooltip } from '@mantine/core';
import {
  IconCopy,
  IconFocus,
  IconLayoutGrid,
  IconMicrophone,
  IconMicrophoneOff,
  IconPhoneOff,
  IconPlayerRecord,
  IconPlayerRecordFilled,
  IconScreenShare,
  IconScreenShareOff,
  IconVideo,
  IconVideoOff,
  IconVolume,
} from '@tabler/icons-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  chimeGridColumns,
  otherScreenShareActive,
  screenShareSupported,
  selectSpeakerTile,
  type ChimeTile,
} from '@/lib/telehealth/chime/chimeTiles';
import {
  participantAvatarColor,
  participantInitials,
} from '@/lib/telehealth/chime/chimeParticipantVisuals';

export type ChimeViewMode = 'speaker' | 'gallery';

function TileVideo({
  tileId,
  bindTile,
  local,
  contain = false,
}: {
  tileId: number;
  bindTile: (tileId: number, el: HTMLVideoElement | null) => void;
  local: boolean;
  /** A shared screen is letterboxed, never cropped. */
  contain?: boolean;
}): JSX.Element {
  const ref = useRef<HTMLVideoElement | null>(null);
  useEffect(() => {
    const el = ref.current;
    bindTile(tileId, el);
    return () => bindTile(tileId, null);
  }, [bindTile, tileId]);
  return (
    <video
      ref={ref}
      autoPlay
      playsInline
      // Audio is mixed into the single <audio> element. An unmuted tile keeps
      // Chrome's tab speaker icon after the tile is detached.
      muted
      // Absolute so the intrinsic video size never contributes to layout — otherwise a
      // 1080p stream pushes the page taller than the viewport.
      style={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        objectFit: contain ? 'contain' : 'cover',
        // Mirror only the self-view, never what remote attendees see.
        transform: local ? 'scaleX(-1)' : undefined,
      }}
    />
  );
}

export function ChimeMediaStage({
  tiles,
  bindTile,
  bindAudio,
  muted,
  cameraOn,
  onToggleMute,
  onToggleCamera,
  onEnd,
  onEndForEveryone,
  activeSpeakerId,
  attendeeExternalUserIds,
  localDisplayName,
  displayNameForExternalUserId,
  onCopyLink,
  recording = false,
  recordingDetail,
  screenShare,
  localAttendeeId,
  endLabel = 'End call',
  endDisabled = false,
  busy = false,
}: {
  tiles: ChimeTile[];
  bindTile: (tileId: number, el: HTMLVideoElement | null) => void;
  bindAudio: (el: HTMLAudioElement | null) => void;
  muted: boolean;
  cameraOn: boolean;
  onToggleMute: () => void;
  onToggleCamera: () => void;
  /** Leave the call (the hang-up button, or Leave call in its menu). */
  onEnd: () => void;
  /** Providers only: the hang-up button opens a menu with Leave call and End session for everyone. */
  onEndForEveryone?: () => void;
  activeSpeakerId?: string;
  attendeeExternalUserIds?: Readonly<Record<string, string>>;
  localDisplayName?: string;
  displayNameForExternalUserId?: (externalUserId: string) => string | undefined;
  onCopyLink?: () => void;
  /** True only while the AWS media-capture pipeline is actually running. */
  recording?: boolean;
  /** Why it is or is not recording (e.g. consent missing); the recording badge's tooltip. */
  recordingDetail?: string;
  /** Share screen for admitted attendees; hidden where the browser cannot capture the screen. */
  screenShare?: { sharing: boolean; onStart: () => void; onStop: () => void };
  /** This browser's attendee, so its own share is not counted as someone else's. */
  localAttendeeId?: string;
  endLabel?: string;
  /** An end is already in progress (e.g. saving notes first). */
  endDisabled?: boolean;
  /** A call action is running; a spinner shows beside the recording badge. */
  busy?: boolean;
}): JSX.Element {
  const [viewMode, setViewMode] = useState<ChimeViewMode>('speaker');
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [audioBlocked, setAudioBlocked] = useState(false);
  useEffect(() => {
    bindAudio(audioRef.current);
    return () => bindAudio(null);
  }, [bindAudio]);

  // iOS Safari (and Chrome without prior engagement) refuses `autoplay` once the join's
  // awaits have consumed the click's user activation. Retry from an explicit tap.
  const playAudio = useCallback(() => {
    const el = audioRef.current;
    if (!el?.srcObject) return;
    el.play().then(
      () => setAudioBlocked(false),
      (e: unknown) => {
        if (e instanceof DOMException && e.name === 'NotAllowedError') setAudioBlocked(true);
      },
    );
  }, []);

  const speaker = selectSpeakerTile(tiles, activeSpeakerId);
  const shareControl = screenShare && screenShareSupported(globalThis.navigator) ? screenShare : undefined;
  // One share at a time: Share screen is disabled while someone else is sharing.
  const otherShare = otherScreenShareActive(tiles, localAttendeeId);
  const speakerThumbnails = speaker ? tiles.filter((tile) => tile.tileId !== speaker.tileId) : [];

  const renderTile = (tile: ChimeTile, thumbnail = false): JSX.Element => {
    const externalUserId = tile.boundAttendeeId
      ? attendeeExternalUserIds?.[tile.boundAttendeeId]
      : undefined;
    const name = tile.localTile
      ? localDisplayName ?? 'You'
      : (externalUserId ? displayNameForExternalUserId?.(externalUserId) : undefined) ??
        'Participant';
    const displayName = tile.isContent ? `${name}'s screen` : name;
    const cameraOff = !tile.isContent && (tile.removed || (tile.localTile && !cameraOn));
    return (
      <Box
        key={tile.tileId}
        style={{
          position: 'relative',
          minWidth: 0,
          minHeight: 0,
          overflow: 'hidden',
          borderRadius: 8,
          background: 'var(--mantine-color-dark-7)',
          ...(thumbnail
            ? { width: 180, height: 102, flex: '0 0 auto' }
            : { width: '100%', height: '100%' }),
        }}
      >
        {!tile.removed ? (
          <TileVideo
            tileId={tile.tileId}
            bindTile={bindTile}
            local={tile.localTile && !tile.isContent}
            contain={tile.isContent}
          />
        ) : null}
        {cameraOff ? (
          <Center pos="absolute" inset={0}>
            <Avatar
              size={thumbnail ? 52 : 96}
              radius="xl"
              color={participantAvatarColor(externalUserId ?? tile.boundAttendeeId ?? displayName)}
              aria-label={`${displayName}, camera off`}
            >
              {participantInitials(displayName)}
            </Avatar>
          </Center>
        ) : null}
        <Text
          size="xs"
          c="white"
          style={{
            position: 'absolute',
            left: 8,
            bottom: 6,
            textShadow: '0 1px 3px rgba(0, 0, 0, 0.9)',
          }}
        >
          {tile.localTile ? 'You' : displayName}
        </Text>
      </Box>
    );
  };

  return (
    <Box
      // `minHeight: 0` lets this shrink below its content inside the page's flex column.
      style={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        overflow: 'hidden',
        background: 'var(--mantine-color-dark-9)',
      }}
    >
      {/* The video area; the control bar below it never covers a participant. */}
      <Box style={{ position: 'relative', flex: 1, minHeight: 0, overflow: 'hidden' }}>
        <audio ref={audioRef} autoPlay onLoadedMetadata={playAudio} />
        {audioBlocked ? (
          <Button
            leftSection={<IconVolume size={16} />}
            onClick={playAudio}
            style={{ position: 'absolute', bottom: 16, left: '50%', transform: 'translateX(-50%)', zIndex: 2 }}
          >
            Tap to enable audio
          </Button>
        ) : null}
        {/* Recording state must always be on screen. */}
        <Group gap="xs" wrap="nowrap" style={{ position: 'absolute', top: 16, left: 16, zIndex: 1 }}>
          {/* Tap and keyboard focus show the reason too, so it is not hover-only (tablets, keyboard users). */}
          <Tooltip
            label={recordingDetail}
            disabled={!recordingDetail}
            withArrow
            position="bottom-start"
            events={{ hover: true, focus: true, touch: true }}
          >
            <Badge
              size="lg"
              radius="sm"
              variant="filled"
              color={recording ? 'red' : 'gray'}
              leftSection={
                recording ? <IconPlayerRecordFilled size={14} /> : <IconPlayerRecord size={14} />
              }
              aria-live="polite"
              // Focusable only when there is a reason; the tooltip then describes the badge (aria-describedby).
              tabIndex={recordingDetail ? 0 : undefined}
            >
              {recording ? 'Recording' : 'Not recording'}
            </Badge>
          </Tooltip>
          {shareControl?.sharing ? (
            <Badge size="lg" radius="sm" variant="filled" color="blue" leftSection={<IconScreenShare size={14} />}>
              You&apos;re sharing your screen
            </Badge>
          ) : null}
          {busy ? <Loader size="sm" color="white" aria-label="Working…" /> : null}
        </Group>
        {tiles.length === 0 ? (
          <Center h="100%">
            <Text c="dimmed">Connecting video…</Text>
          </Center>
        ) : viewMode === 'gallery' ? (
          <Box
            style={{
              display: 'grid',
              boxSizing: 'border-box',
              height: '100%',
              gap: 8,
              padding: 8,
              gridTemplateColumns: `repeat(${chimeGridColumns(tiles.length)}, minmax(0, 1fr))`,
              gridAutoRows: 'minmax(0, 1fr)',
            }}
          >
            {tiles.map((tile) => renderTile(tile))}
          </Box>
        ) : (
          <Box h="100%" p={8} style={{ position: 'relative' }}>
            {speaker ? renderTile(speaker) : null}
            {speakerThumbnails.length ? (
              <Group
                gap={8}
                wrap="nowrap"
                style={{
                  position: 'absolute',
                  top: 16,
                  right: 16,
                  maxWidth: 'calc(100% - 32px)',
                  overflowX: 'auto',
                }}
              >
                {speakerThumbnails.map((tile) => renderTile(tile, true))}
              </Group>
            ) : null}
          </Box>
        )}
      </Box>
      <Group
        gap="md"
        px="lg"
        py="sm"
        justify="center"
        style={{ flex: '0 0 auto', background: 'var(--mantine-color-dark-8)' }}
      >
        <Tooltip label={muted ? 'Unmute' : 'Mute'}>
          <ActionIcon
            size="xl"
            radius="xl"
            variant="filled"
            color={muted ? 'red' : 'gray'}
            aria-label="Mute microphone"
            aria-pressed={muted}
            onClick={onToggleMute}
          >
            {muted ? <IconMicrophoneOff size={22} /> : <IconMicrophone size={22} />}
          </ActionIcon>
        </Tooltip>
        <Tooltip label={cameraOn ? 'Turn camera off' : 'Turn camera on'}>
          <ActionIcon
            size="xl"
            radius="xl"
            variant="filled"
            color={cameraOn ? 'gray' : 'red'}
            aria-label="Turn camera off"
            aria-pressed={!cameraOn}
            onClick={() => void onToggleCamera()}
          >
            {cameraOn ? <IconVideo size={22} /> : <IconVideoOff size={22} />}
          </ActionIcon>
        </Tooltip>
        <Tooltip label={viewMode === 'speaker' ? 'Gallery view' : 'Speaker view'}>
          <ActionIcon
            size="xl"
            radius="xl"
            variant="filled"
            color="gray"
            aria-label={viewMode === 'speaker' ? 'Gallery view' : 'Speaker view'}
            onClick={() => setViewMode((current) => (current === 'speaker' ? 'gallery' : 'speaker'))}
          >
            {viewMode === 'speaker' ? <IconLayoutGrid size={22} /> : <IconFocus size={22} />}
          </ActionIcon>
        </Tooltip>
        {shareControl ? (
          <Tooltip
            label={
              shareControl.sharing ? 'Stop sharing' : otherShare ? 'Someone else is sharing' : 'Share screen'
            }
          >
            <ActionIcon
              size="xl"
              radius="xl"
              variant="filled"
              color={shareControl.sharing ? 'blue' : 'gray'}
              aria-label={shareControl.sharing ? 'Stop sharing' : 'Share screen'}
              disabled={!shareControl.sharing && otherShare}
              onClick={shareControl.sharing ? shareControl.onStop : shareControl.onStart}
            >
              {shareControl.sharing ? <IconScreenShareOff size={22} /> : <IconScreenShare size={22} />}
            </ActionIcon>
          </Tooltip>
        ) : null}
        {onCopyLink ? (
          <Tooltip label="Copy meeting link">
            <ActionIcon
              size="xl"
              radius="xl"
              variant="filled"
              color="gray"
              aria-label="Copy meeting link"
              onClick={onCopyLink}
            >
              <IconCopy size={22} />
            </ActionIcon>
          </Tooltip>
        ) : null}
        {onEndForEveryone ? (
          // Two clicks to end: the menu's choices act at once.
          <Menu position="top" withinPortal>
            <Menu.Target>
              <ActionIcon
                size="xl"
                radius="xl"
                variant="filled"
                color="red"
                aria-label="Hang up"
                disabled={endDisabled}
              >
                <IconPhoneOff size={22} />
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item onClick={onEnd}>Leave call</Menu.Item>
              <Menu.Item color="red" onClick={onEndForEveryone}>
                End session for everyone
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        ) : (
          <Tooltip label={endLabel}>
            <ActionIcon
              size="xl"
              radius="xl"
              variant="filled"
              color="red"
              aria-label={endLabel}
              disabled={endDisabled}
              onClick={onEnd}
            >
              <IconPhoneOff size={22} />
            </ActionIcon>
          </Tooltip>
        )}
      </Group>
    </Box>
  );
}
